import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@vision/ui/enums';
import type { ApiResponse } from '@vision/ui/interfaces';
import type { BenchmarkSetup, BenchmarkTechnicalInput } from '../pages/Benchmarking/types';
import { axiosBaseQuery } from './base.service';
import {
  fromSetupResponse,
  toCreateRequest,
  toDisplayRequest,
  toFlowMappingRequest,
  toMappingRequest,
  toTechnicalRequest,
} from './benchmark-setups.mapper';
import type { BenchmarkSetupResponseDto } from './benchmark-setups.types';

type SetupArgs = { accountId: string; setupId: string };
type SaveSetupArgs = SetupArgs &
  (
    | { operation: 'technical'; input: BenchmarkTechnicalInput }
    | { operation: 'activate' }
    | { operation: 'mapping'; nodeId: string; flows: { flowId: string; measureMappings: Record<string, string> }[] }
    | { operation: 'flow'; flowId: string; measureMappings: Record<string, string> }
    | {
        operation: 'display';
        name: string;
        measures: { id: string; name: string }[];
        levels: { id: string; name: string }[];
      }
  );
const collectionUrl = (accountId: string) => `/v2/accounts/${accountId}/reporting/benchmark_setups`;
const normalize = (response: ApiResponse<BenchmarkSetupResponseDto>) => fromSetupResponse(response.data);

export const BenchmarkSetupsService = createApi({
  reducerPath: 'BenchmarkSetupsService',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['BenchmarkSetups', 'BenchmarkWaves'],
  endpoints: (builder) => ({
    benchmarkSetupList: builder.query<ApiResponse<BenchmarkSetup[]>, { accountId: string; query: string }>({
      query: ({ accountId, query }) => {
        const params = new URLSearchParams(query);
        const allowed = [
          'status',
          'customer_success_status',
          'search_string',
          'page',
          'per_page',
          'sort_by',
          'sort_order',
        ];
        for (const key of [...params.keys()]) if (!allowed.includes(key)) params.delete(key);
        return { url: `${collectionUrl(accountId)}?${params}`, method: HttpMethod.GET };
      },
      transformResponse: (response: ApiResponse<BenchmarkSetupResponseDto[]>) => ({
        ...response,
        data: response.data.map(fromSetupResponse),
      }),
      providesTags: (_result, _error, { accountId }) => [{ type: 'BenchmarkSetups', id: accountId }],
    }),
    benchmarkSetups: builder.query<BenchmarkSetup[], string>({
      async queryFn(accountId, _api, _options, baseQuery) {
        const setups: BenchmarkSetup[] = [];
        let page: number | null = 1;
        do {
          const result = await baseQuery({
            url: `${collectionUrl(accountId)}?page=${page}&per_page=20`,
            method: HttpMethod.GET,
          });
          if (result.error) return { error: result.error };
          const response = result.data as ApiResponse<BenchmarkSetupResponseDto[]>;
          setups.push(...response.data.map(fromSetupResponse));
          page = response.meta?.pagination.next ?? null;
        } while (page);
        return { data: setups };
      },
      providesTags: (_result, _error, accountId) => [{ type: 'BenchmarkSetups', id: accountId }],
    }),
    benchmarkSetup: builder.query<BenchmarkSetup, SetupArgs>({
      query: ({ accountId, setupId }) => ({ url: `${collectionUrl(accountId)}/${setupId}`, method: HttpMethod.GET }),
      transformResponse: normalize,
    }),
    createBenchmarkSetup: builder.mutation<BenchmarkSetup, { accountId: string; key: string; name: string }>({
      query: (args) => ({
        url: collectionUrl(args.accountId),
        method: HttpMethod.POST,
        data: toCreateRequest(args),
        extraOptions: { notification: { autoHandleError: false, autoHandleSuccess: false } },
      }),
      transformResponse: normalize,
      invalidatesTags: (_result, _error, { accountId }) => [{ type: 'BenchmarkSetups', id: accountId }],
    }),
    saveBenchmarkSetup: builder.mutation<BenchmarkSetup, SaveSetupArgs>({
      query: (args) => {
        const base = `${collectionUrl(args.accountId)}/${args.setupId}`;
        const method = HttpMethod.POST;
        const extraOptions = { notification: { autoHandleError: false, autoHandleSuccess: false } };
        switch (args.operation) {
          case 'technical':
            return {
              url: `${base}/technical_configuration`,
              method,
              extraOptions,
              data: toTechnicalRequest(args.input),
            };
          case 'activate':
            return { url: `${base}/activate`, method, extraOptions, data: {} };
          case 'mapping':
            return {
              url: `${base}/customer_success_configuration`,
              method,
              extraOptions,
              data: toMappingRequest(args),
            };
          case 'flow':
            return { url: `${base}/flow_mappings`, method, extraOptions, data: toFlowMappingRequest(args) };
          case 'display':
            return { url: `${base}/display_configuration`, method, extraOptions, data: toDisplayRequest(args) };
        }
      },
      transformResponse: normalize,
      invalidatesTags: (_result, _error, { accountId }) => [{ type: 'BenchmarkSetups', id: accountId }],
    }),
    archiveBenchmarkSetup: builder.mutation<void, SetupArgs>({
      query: ({ accountId, setupId }) => ({
        url: `${collectionUrl(accountId)}/${setupId}/archive`,
        method: HttpMethod.POST,
        extraOptions: { notification: { autoHandleError: false, autoHandleSuccess: false } },
      }),
      invalidatesTags: (_result, error, { accountId }) => (error ? [] : [{ type: 'BenchmarkSetups', id: accountId }]),
    }),
    benchmarkTimezones: builder.query<string[], void>({
      query: () => ({ url: '/v1/timezones', method: HttpMethod.GET }),
    }),
  }),
});
export const {
  useBenchmarkSetupListQuery,
  useBenchmarkSetupsQuery,
  useLazyBenchmarkSetupsQuery,
  useLazyBenchmarkSetupQuery,
  useCreateBenchmarkSetupMutation,
  useSaveBenchmarkSetupMutation,
  useBenchmarkTimezonesQuery,
  useArchiveBenchmarkSetupMutation,
} = BenchmarkSetupsService;
